
import './record-edit-screen.scss'

import { faArrowLeft, faEye, faTabletAlt } from '@fortawesome/free-solid-svg-icons';
import { cloneDeep, set } from 'lodash';
import { catchError, first, switchMap, tap, take } from 'rxjs/operators';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import { forkJoin, of, empty } from 'rxjs';
import { MediaTransferHelper } from '@skinsoft/media-transfer-helper';
import { AuditCampaign, AuditCampaignDataStore, Item, ItemDataStore, itemSelectors, LocationDataStore, locationSelectors, Record, RecordDataStore, RecordLifecycleState, recordSelectors } from '@skinsoft/entity';
import { AuditCampaignFeatured, RecordFeatured, toCampaignFeatured$, toRecordFeatured$ } from '@skinsoft/entity-ui';
import { ReactiveVue } from '@skinsoft/reactive-vue';
import { RecordMask, RecordMaskDisplay } from '@skinsoft/record-mask';
import { toSearchFeatured$ } from '@skinsoft/search-data-store/src/featured';
import { CollapsibleHeader, getTranslatedString, SearchFeatured } from '@skinsoft/ui';
import { Media } from '@skinsoft/ui/src/item-card/media';

import { DISPLAY_ALERT_TOPIC, FORCE_CLOSE_MENU_TOPIC, SAVE_CURRENT_RECORD_TOPIC, TRANSITION_RECORD_TO_STATE } from '../../../busTopics';
import { auditRecord_recordMask } from '../../../local-record-masks';
import { Routes } from '../../../router';
import { ItemEditScreen } from '../item-edit-screen';
import _ from 'lodash';

@Component({
    components: {
        sCollapsibleHeader: CollapsibleHeader,
        sItemEditScreen: ItemEditScreen,
        sMedia: Media,
        sRecordMaskDisplay: RecordMaskDisplay
    },
    props: ['recordId', 'campaignId'],
    template: require('./record-edit-screen.html')
})
export class RecordEditScreen extends ReactiveVue {

    public recordId: string;
    public campaignId: string;

    private campaign: AuditCampaign = null as any;
    private record: Record = null as any;
    private item: Item = null as any;

    // Copy of the record used for saving, it avoids unnecessary rerender of components
    private recordToSave: Record = null as any;

    private campaignFeatured: AuditCampaignFeatured = null as any;
    private recordFeatured: RecordFeatured = null as any;
    private itemFeatured: SearchFeatured = null as any;

    private itemLocation: string = "";
    private displayItemScreen: boolean = false;
    private overrideFromUpdateCache = false;

    private recordMask: RecordMask = auditRecord_recordMask;

    // Icons 
    private backIcon = faArrowLeft;
    private eyeIcon = faEye;
    private localIcon = faTabletAlt;

    private $auditCampaignDataStore: AuditCampaignDataStore
    private $recordDataStore: RecordDataStore;
    private $itemDataStore: ItemDataStore;
    private $locationDataStore: LocationDataStore;
    private $mediaTransferHelper: MediaTransferHelper;

    private saveSuccessMessage: string = ""
    private saveErrorMessage: string = ""

    public mounted() {
        this.subscribeToBus(SAVE_CURRENT_RECORD_TOPIC, this.saveMediaAndRecord)
        this.subscribeToBus(TRANSITION_RECORD_TO_STATE, (event) => this.transitionRecord(event.payload.state))
        this.saveSuccessMessage = this.$t('editRecordScreen.notifications.saveSuccess') as string;
        this.saveErrorMessage = this.$t('editRecordScreen.notifications.saveError') as string;
    }

    public beforeDestroy() {
        super.beforeDestroy();
    }

    private saveMediaAndRecord() {
        const locale = this.$i18n.locale;
        forkJoin(_.get(this.record,'associatedMedias.medias', []).map( e => {
            if(e.startsWith("local:") && window.navigator.onLine){
                return this.$mediaTransferHelper.downloadMedia(e).pipe(take(2))
            }
            return of(e)
        })).subscribe( () => {
                _.set(this.recordToSave,'associatedMedias',  _.get(this.record,'associatedMedias'))
                this.saveRecord()
            }, 
            () => {},
            () => {
                _.set(this.recordToSave,'associatedMedias',  _.get(this.record,'associatedMedias'))
                this.saveRecord()
            }

        )
    
    }
    private get isReadOnly() {
        const recordState = recordSelectors.currentState(this.record, this.$i18n.locale);
        return recordState === 'validated' || recordState === 'toValidated'
    }

    private saveRecord() {
        const locale = this.$i18n.locale;
        this.$recordDataStore.update(this.recordId, this.recordToSave)
            .pipe(first(),
                catchError(
                    err => {
                        const alertPayload = {
                            message: this.saveErrorMessage,
                            variant: "danger"
                        };
                        this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
                        return empty()
                    }
                ))
            .subscribe(
                response => {
                    this.overrideFromUpdateCache = !!response.fromUpdateCache;

                    const alertPayload = {
                        message: this.saveSuccessMessage,
                        variant: "success"
                    };

                    this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
                }
            );
    }

    private subscribe() {
        if (this.campaignId && this.recordId) {
            const locale = this.$i18n.locale;
            this.subscribeTo(
                this.$auditCampaignDataStore.fetchOne(this.campaignId).pipe(
                    switchMap(campaign => toCampaignFeatured$(campaign, locale))
                ),
                campaignFeatured => {
                    this.campaignFeatured = campaignFeatured;
                },
                'campaign-subscription')

            this.subscribeTo(
                this.$recordDataStore.fetchOne(this.recordId).pipe(
                    tap(
                        record => {
                            this.record = record
                            this.recordToSave = cloneDeep(record);
                        }
                    ),
                    switchMap(record => toRecordFeatured$(record, locale)),
                    switchMap( // Keeping record data
                        recordFeatured => {
                            this.recordFeatured = recordFeatured;
                            return this.$itemDataStore.fetchOne(recordFeatured.linkedItemId)
                        }
                    ),
                    tap( // Fetching location
                        async item => {
                            const locId = itemSelectors.realTimeLocation(item);
                            if (locId) {
                                const loc = await (this.$locationDataStore.fetchOne(locId).pipe(first()).toPromise());
                                this.itemLocation = locationSelectors.translatedTitle(loc, this.$i18n.locale);
                            }
                        }
                    ),
                    switchMap(item => { // Keeping item data
                        this.item = item;
                        return toSearchFeatured$({
                            expression: null,
                            item,
                            manifestation: null,
                            work: null,
                        }, locale, false)
                    })
                ),
                itemFeatured => {
                    this.itemFeatured = itemFeatured;
                },
                'record-subscription')
        }
    }

    private get fromUpdateCache() {
        return this.recordFeatured?.fromUpdateCache || this.overrideFromUpdateCache;
    }

    private transitionRecord(recordLifecycleState: RecordLifecycleState) {
        const errorMessage = this.$t('editRecordScreen.notifications.stateTransitionError');
        const sucessMessage = this.$t('editRecordScreen.notifications.stateTransitionSuccess');
        this.$recordDataStore.transitionRecord(this.record.id, recordLifecycleState)
            .then(
                () => {
                    this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                        message: sucessMessage,
                        variant: "success"
                    })
                    this.goBack()
                }
            ).catch(
                () => {
                    this.emitOnBus(DISPLAY_ALERT_TOPIC, {
                        message: errorMessage,
                        variant: "danger"
                    })
                }
            )
    }

    private get itemPath() {

        if (this.campaignFeatured && this.recordFeatured && this.itemFeatured) {
            return `${this.campaignFeatured.identificationNumber} > ${this.recordFeatured.recordIdentifier} > ${this.itemFeatured.inventoryNumber} `
        }
    }

    private openItemEditScreen() {
        this.saveMediaAndRecord();
        this.displayItemScreen = true
    }

    private updateRecord(updateEvent) {
        set(this.recordToSave, updateEvent.fieldPath, updateEvent.value);
    }

    private goBack() {
        this.$router.push({ name: Routes.CAMPAIGN, params: { campaignId: this.campaignId } });
        this.emitOnBus(FORCE_CLOSE_MENU_TOPIC)
    }

    private unsubscribe() {
        this.unsubscribeFrom('record-subscription')
        this.unsubscribeFrom('campaign-subscription')
    }

    @Watch('$route', { immediate: true })
    private onRouteChange() {
        this.unsubscribe();
        this.subscribe();
    }

    private onAddMedia(media){
        if(!_.get(this.record, 'associatedMedias.medias', []).includes(media.mediaId)) {
            if(_.get(this.record, 'associatedMedias.medias') === null){
                _.set(this.record, 'associatedMedias.medias', [media.mediaId]);
            }else{
                _.get(this.record, 'associatedMedias.medias').push(media.mediaId);
            }
        } 
    }

}